import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
// @ts-ignore - plotly.js-dist-min doesn't have type declarations
import Plotly from "plotly.js-dist-min";
import styles from "./3DChart.module.css";


interface Chart3DData {
  Title?: string;
  [key: string]: number[] | number[][] | string | undefined;
}

interface Surface3DChartProps {
  data?: Chart3DData;
  height?: number | string;
  colorscale?: string;
}

// Elegant custom colorscale — cool blues to warm amber
const CUSTOM_COLORSCALE: [number, string][] = [
  [0.0, "#0d1b2a"],
  [0.15, "#1b3a5c"],
  [0.3, "#2a6f97"],
  [0.45, "#40a8c4"],
  [0.55, "#61d4b3"],
  [0.65, "#a8e06c"],
  [0.75, "#f0c929"],
  [0.85, "#f4845f"],
  [0.95, "#e63946"],
  [1.0, "#d00000"],
];

// Camera presets for quick navigation (zoomed in closer)
// All presets use up:{z:1} so turntable mode keeps Z-axis always vertical.
// Top view uses a tiny x-offset to avoid gimbal lock when looking straight down.
const CAMERA_PRESETS = {
  default: { eye: { x: 1.15, y: -1.45, z: 0.5 }, center: { x: 0, y: 0, z: -0.1 }, up: { x: 0, y: 0, z: 1 } },
  top:     { eye: { x: 0.01, y: -0.01, z: 2.2 }, center: { x: 0, y: 0, z: 0 }, up: { x: 0, y: 0, z: 1 } },
  front:   { eye: { x: 0.0, y: -2.0, z: 0.3 }, center: { x: 0, y: 0, z: 0 }, up: { x: 0, y: 0, z: 1 } },
  side:    { eye: { x: 2.0, y: 0.0, z: 0.3 }, center: { x: 0, y: 0, z: 0 }, up: { x: 0, y: 0, z: 1 } },
} as const;

const ZOOM_STEP = 0.8; // multiplier per zoom-in click (< 1 = closer)


function extractAxisData(data: Chart3DData): {
  xs: number[];
  ys: number[];
  z: (number | null)[][];
  xLabel: string;
  yLabel: string;
  zLabel: string;
} | null {
  const reservedKeys = ["Title"];
  const keys = Object.keys(data).filter((k) => !reservedKeys.includes(k));

  const array1DKeys: string[] = [];
  const array2DKeys: string[] = [];

  for (const key of keys) {
    const val = data[key];
    if (Array.isArray(val)) {
      if (val.length > 0 && Array.isArray(val[0])) {
        array2DKeys.push(key);
      } else if (val.length > 0 && typeof val[0] === "number") {
        array1DKeys.push(key);
      }
    }
  }

  if (array1DKeys.length < 2 || array2DKeys.length < 1) {
    return null;
  }

  const xLabel = array1DKeys[0];
  const yLabel = array1DKeys[1];
  const zLabel = array2DKeys[0];

  // Replace zero (and near-zero) MidIV values with null so Plotly skips them
  const rawZ = data[zLabel] as number[][];
  const filteredZ: (number | null)[][] = rawZ.map((row) =>
    row.map((v) => (v === 0 || v === null || v === undefined ? null : v))
  );

  return {
    xs: data[xLabel] as number[],
    ys: data[yLabel] as number[],
    z: filteredZ,
    xLabel,
    yLabel,
    zLabel,
  };
}

// Declare Plotly on window for TypeScript
declare global {
  interface Window {
    Plotly: {
      react: (el: HTMLElement, data: object[], layout: object, config: object) => Promise<void>;
      Plots: { resize: (el: HTMLElement) => void };
      purge: (el: HTMLElement) => void;
    };
  }
}

interface PlotlySurfaceProps {
  surfaceTrace: object;
  layout: object;
  config: object;
  plotRef?: React.MutableRefObject<HTMLDivElement | null>;
}

function PlotlySurface({ surfaceTrace, layout, config, plotRef }: PlotlySurfaceProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const plottedRef = useRef(false);

  // Expose ref to parent
  useEffect(() => {
    if (plotRef && hostRef.current) {
      plotRef.current = hostRef.current;
    }
  }, [plotRef]);

  // Resize observer for responsive chart
  useEffect(() => {
    if (!hostRef.current) return;
    const el = hostRef.current;

    const ro = new ResizeObserver(() => {
      try {
        Plotly.Plots.resize(el);
      } catch {
        // ignore
      }
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Render Plotly chart
  useEffect(() => {
    if (!hostRef.current) return;
    const el = hostRef.current;

    const safeLayout = { ...layout, uirevision: "static" };
    plottedRef.current = true;
    Plotly.react(el, [surfaceTrace], safeLayout, config);

    return () => {
      if (plottedRef.current) {
        try {
          Plotly.purge(el);
        } catch {
          // ignore
        }
      }
    };
  }, [surfaceTrace, layout, config]);

  return <div ref={hostRef} className={styles.fullSize} />;
}


export const Surface3DChart: React.FC<Surface3DChartProps> = ({
  data = {},
  height,
  colorscale,
}) => {
  const plotElRef = useRef<HTMLDivElement | null>(null);
  const [activeView, setActiveView] = useState<string>("default");
  const zoomRef = useRef(1); // tracks cumulative zoom scale

  // Extract axis data from flexible dictionary format
  const axisData = useMemo(() => extractAxisData(data), [data]);

  // Derive chart title from data
  const chartTitle = data.Title || "";

  // Resolve colorscale
  const resolvedColorscale = colorscale || CUSTOM_COLORSCALE;

  // Build Plotly surface trace — smooth, clean, minimal
  const surfaceTrace = useMemo(() => {
    if (!axisData) return null;

    const { xs, ys, z, xLabel, yLabel, zLabel } = axisData;

    return {
      type: "surface",
      x: xs,
      y: ys,
      z: z,
      colorscale: resolvedColorscale,
      showscale: false,
      opacity: 1.0,
      hovertemplate:
        `<b>${xLabel}</b>: %{x:.1f}<br>` +
        `<b>${yLabel}</b>: %{y:.1f}<br>` +
        `<b>${zLabel}</b>: %{z:.4f}<extra></extra>`,
      flatshading: false,
      lighting: {
        ambient: 0.65,
        diffuse: 0.8,
        roughness: 0.5,
        specular: 0.3,
        fresnel: 0.2,
      },
      lightposition: { x: 100, y: -80, z: 200 },
      contours: {
        x: { show: false },
        y: { show: false },
        z: {
          show: true,
          usecolormap: true,
          highlightcolor: "rgba(255,255,255,0.15)",
          project: { z: false },
          width: 1,
        },
      },
    };
  }, [axisData, resolvedColorscale]);

  // Shared axis style (DRY)
  const axisStyle = useMemo(() => ({
    gridcolor: "rgba(255,255,255,0.04)",
    zerolinecolor: "rgba(255,255,255,0.06)",
    showbackground: true,
    backgroundcolor: "rgba(0,0,0,0)",
    tickfont: { size: 10, color: "rgba(255,255,255,0.5)", family: "'Inter','SF Pro Display',system-ui,sans-serif" },
    titlefont: { size: 11, color: "rgba(255,255,255,0.65)", family: "'Inter','SF Pro Display',system-ui,sans-serif" },
    showspikes: false,
    showline: false,
    zeroline: false,
    nticks: 6,
  }), []);

  // Build Plotly layout — minimal, dark, clean
  const layout = useMemo(() => {
    const xLabel = axisData?.xLabel || "x";
    const yLabel = axisData?.yLabel || "y";
    const zLabel = axisData?.zLabel || "z";

    return {
      autosize: true,
      margin: { l: 0, r: 0, t: 0, b: 0 },
      paper_bgcolor: "rgba(0,0,0,0)",
      scene: {
        camera: CAMERA_PRESETS.default,
        xaxis: { ...axisStyle, title: { text: xLabel, standoff: 10 } },
        yaxis: { ...axisStyle, title: { text: yLabel, standoff: 10 } },
        zaxis: { ...axisStyle, title: { text: zLabel, standoff: 10 } },
        bgcolor: "rgba(0,0,0,0)",
        dragmode: "turntable",
        aspectmode: "manual",
        aspectratio: { x: 1.2, y: 1.2, z: 0.7 },
      },
      hoverlabel: {
        bgcolor: "rgba(15,15,25,0.92)",
        bordercolor: "rgba(255,255,255,0.15)",
        font: { size: 12, color: "#fff", family: "'Inter','SF Pro Display',system-ui,sans-serif" },
      },
    };
  }, [axisData, axisStyle]);

  // Plotly config — clean, no clutter
  const config = useMemo(
    () => ({
      responsive: true,
      displaylogo: false,
      displayModeBar: false,
      scrollZoom: true,
    }),
    []
  );

  // Animate camera to a preset
  const animateToView = useCallback((viewName: string) => {
    if (!plotElRef.current) return;
    const preset = CAMERA_PRESETS[viewName as keyof typeof CAMERA_PRESETS];
    if (!preset) return;

    zoomRef.current = 1; // reset zoom when switching presets
    setActiveView(viewName);
    Plotly.relayout(plotElRef.current, {
      "scene.camera": preset,
    });
  }, []);

  // Zoom in/out by scaling the camera eye distance
  const handleZoom = useCallback((direction: "in" | "out") => {
    if (!plotElRef.current) return;

    const factor = direction === "in" ? ZOOM_STEP : 1 / ZOOM_STEP;
    zoomRef.current *= factor;

    // Read current camera from the live layout
    const el = plotElRef.current as any;
    const currentCamera = el._fullLayout?.scene?._scene?.getCamera?.() ||
      el.layout?.scene?.camera ||
      CAMERA_PRESETS.default;

    const eye = currentCamera.eye || CAMERA_PRESETS.default.eye;

    Plotly.relayout(plotElRef.current, {
      "scene.camera.eye": {
        x: eye.x * factor,
        y: eye.y * factor,
        z: eye.z * factor,
      },
    });
  }, []);

  // Handle missing or invalid data
  if (!axisData || !surfaceTrace) {
    return (
      <div className={styles.chartRoot} style={{ height: height || "500px" }}>
        <div className={styles.chartInner}>
          <div className={styles.chartCard}>
            <div className={styles.loadingState}>
              No valid 3D data provided. Expected format: two 1D arrays (x, y) and one 2D array (z).
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chartRoot} style={{ height: height || "auto" }}>
      <div className={styles.chartInner}>
        <div className={styles.chartCard}>
          {chartTitle && (
            <div className={styles.chartHeader}>
              <div className={styles.chartTitle}>{chartTitle}</div>
              <div className={styles.chartSubtitle}>
                Drag to rotate · Scroll to zoom · Right-drag to pan
              </div>
            </div>
          )}

          <div className={styles.plotWrapper}>
            {/* Camera preset + zoom buttons */}
            <div className={styles.viewControls}>
              {(["default", "top", "front", "side"] as const).map((view) => (
                <button
                  key={view}
                  className={`${styles.viewBtn} ${activeView === view ? styles.viewBtnActive : ""}`}
                  onClick={() => animateToView(view)}
                  title={`${view.charAt(0).toUpperCase() + view.slice(1)} view`}
                >
                  {view === "default" ? "3D" : view.charAt(0).toUpperCase() + view.slice(1)}
                </button>
              ))}
              <span className={styles.viewDivider} />
              <button
                className={styles.viewBtn}
                onClick={() => handleZoom("in")}
                title="Zoom in"
              >
                +
              </button>
              <button
                className={styles.viewBtn}
                onClick={() => handleZoom("out")}
                title="Zoom out"
              >
                −
              </button>
            </div>

            <div className={styles.plotAspect}>
              <div className={styles.plotFill}>
                <PlotlySurface
                  surfaceTrace={surfaceTrace}
                  layout={layout}
                  config={config}
                  plotRef={plotElRef}
                />
              </div>
            </div>
          </div>

          <div className={styles.chartFooter}>
            {axisData.xLabel} × {axisData.yLabel} → {axisData.zLabel}
          </div>
        </div>
      </div>
    </div>
  );
};
